import { inject, Injectable } from '@angular/core';
import { CompoApp } from '../../services/CompoApp';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { DocumentGroupModel } from '../../models/document/document-group.model';
import { ResetStateDefaults } from '../base-action-class.model';
import { FilesDetailAction } from './files-detail.action';
import { finalize, from, throwError } from 'rxjs';
import { Observable } from 'rxjs/internal/Observable';
import { map } from 'rxjs/operators';
import { RFileXsState } from '../user-rights/topic-based/file/r-file-xs.state';

interface FilesDetailStateModel {
  documentGroup: DocumentGroupModel;
  readOnly: boolean;
  inProgress: boolean;
}

const getDefaults = (): FilesDetailStateModel => ({
  documentGroup: null,
  readOnly: false,
  inProgress: false
});

@State<FilesDetailStateModel>({
  name: 'filesDetailStateModel',
  defaults: getDefaults()
})
@Injectable()
export class FilesDetailXsState {

  //region INJECTIONS
  private app = inject(CompoApp);
  private store = inject(Store);
  //endregion

  @Selector()
  static id(state: FilesDetailStateModel): string | null {
    return state.documentGroup?.id;
  }

  @Selector()
  static documentGroup(state: FilesDetailStateModel): DocumentGroupModel | null {
    return state.documentGroup;
  }

  @Action(FilesDetailAction.Fetch)
  public fetchDocumentGroup(ctx: StateContext<FilesDetailStateModel>,
                            action: FilesDetailAction.Fetch): Observable<void> {

    const state: FilesDetailStateModel = ctx.getState();

    if (state.inProgress) {
      return throwError(() => new Error('Files detail request is in progress!'));
    }
    if (!action.id) {
      return throwError(() => new Error('DocumentGroup id cannot be null!'));
    }

    ctx.patchState({
      ...getDefaults(),
      inProgress: true
    });

    return from(this.app.documentGroupService.getById(action.id))
      .pipe(
        map((documentGroupModel: DocumentGroupModel): void => {

          ctx.patchState({
            documentGroup: documentGroupModel
          });
        }),
        finalize(() => ctx.patchState({ inProgress: false }))
      );
  }

  @Action(FilesDetailAction.Update)
  public update(ctx: StateContext<FilesDetailStateModel>,
                action: FilesDetailAction.Update): Observable<void> {

    const state: FilesDetailStateModel = ctx.getState();

    if (state.inProgress) {
      return throwError(() => new Error('Files detail request is in progress!'));
    }
    if (!action.documentGroup) {
      return throwError(() => new Error('DocumentGroup cannot be null!'));
    }

    const editableByUser = this.store
      .selectSnapshot(RFileXsState.canEditFileByDocumentGroup(state.documentGroup));

    if (!editableByUser) {
      return throwError(() => new Error('DocumentGroup cannot be updated - missing privilege!'));
    }

    ctx.patchState({
      ...getDefaults(),
      inProgress: true
    });

    return from(this.app.documentGroupService.updateDocumentGroupBasicInfo(action.documentGroup))
      .pipe(
        map((documentGroupModel: DocumentGroupModel): void => {
          ctx.patchState({
            documentGroup: documentGroupModel
          });
        }),
        finalize(() => ctx.patchState({ inProgress: false }))
      );
  }

  @Action(FilesDetailAction.Delete)
  public delete(ctx: StateContext<FilesDetailStateModel>,
                action: FilesDetailAction.Delete): Observable<void> {

    const state: FilesDetailStateModel = ctx.getState();

    if (state.inProgress) {
      return throwError(() => new Error('Files detail request is in progress!'));
    }

    const deletableByUser = this.store
      .selectSnapshot(RFileXsState.canDeleteFileByDocumentGroup(state.documentGroup));

    if (!deletableByUser) {
      return throwError(() => new Error('DocumentGroup cannot be deleted - missing privilege!'));
    }

    ctx.patchState({
      ...getDefaults(),
      inProgress: true
    });

    return from(this.app.documentGroupService.deleteDocumentGroup(state.documentGroup?.id))
      .pipe(
        map((): void => {

        }),
        finalize(() => ctx.patchState({ inProgress: false }))
      );
  }

  @Action([
    FilesDetailAction.ResetState,
    ResetStateDefaults
  ])
  public resetState(ctx: StateContext<FilesDetailStateModel>): void {
    ctx.setState(getDefaults);
  }
}
